import { Product, TrackingKey } from '@evenfinancial/even-ts-static'
import { Middleware } from 'redux'
import { ddLogs } from 'embed/constants/config'
import { configCompanyUuidSelector, configProductSelector, configResultsUuidSelector } from 'embed/selectors/config'
import { datadogLogs } from 'embed/services/Datadog/Logs'
import { datadogRum } from 'embed/services/Datadog/RUM'
import { RUMContextKeys } from 'embed/services/Datadog/RUM/constants'
import Session from 'embed/services/Session'
import { actions as experimentActions, experimentStateSelector, name, slice } from '../slice'
import { FeatureName, Original, VariantName } from '../types'

const EXPERIMENTS_EVENT_CATEGORY = 'experiments'
const EXPERIMENTS_ACTION_ACTIVATED = 'activated'

type ExperimentActivatedContextEventContext = {
  company_uuid?: string
  lead_uuid?: string
  product_type?: Product
  results_uuid?: string
  session_uuid?: string
}

interface ExperimentActivatedContext {
  event_action: typeof EXPERIMENTS_ACTION_ACTIVATED
  event_category: typeof EXPERIMENTS_EVENT_CATEGORY
  event_context: ExperimentActivatedContextEventContext & {
    activated_at: string
    experiment_name: FeatureName
    lead_uuid: string
    variant_name: VariantName
  }
}

const experimentLogger = datadogLogs.createLogger('experiments', {
  context: {
    env: ddLogs.env,
    version: ddLogs.version,
  },
  handler: 'http',
  level: 'info',
})

function logExperimentActivated(
  experimentName: FeatureName,
  variantName: VariantName,
  additionalContext?: ExperimentActivatedContextEventContext,
) {
  const currentDate = new Date()
  experimentLogger.info(`${EXPERIMENTS_EVENT_CATEGORY}:${EXPERIMENTS_ACTION_ACTIVATED}`, {
    event_action: EXPERIMENTS_ACTION_ACTIVATED,
    event_category: EXPERIMENTS_EVENT_CATEGORY,
    event_context: {
      activated_at: currentDate.toISOString(),
      company_uuid: additionalContext?.company_uuid,
      experiment_name: experimentName,
      lead_uuid: additionalContext?.lead_uuid ?? Session.getLeadUuid(),
      session_uuid: additionalContext?.session_uuid ?? Session.sessionUuid,
      variant_name: variantName,
    },
  } as ExperimentActivatedContext)
}

export const experimentMiddleware: Middleware<{}, { [name]: ReturnType<typeof slice.reducer> }> =
  (store) => (next) => (action) => {
    let shouldUpdateContext = false
    let currentState
    switch (action.type) {
      case experimentActions.startExperiment.type:
        const experiment = experimentStateSelector(store.getState())?.[action.payload]
        if (experiment?.enabled === true && experiment.hasError === false && experiment.started === false) {
          action.meta = {
            ...action.meta,
            timestamp: new Date(),
            trackingData: {
              experiment_name: action.payload,
              selected_variant: experiment.variantName,
            },
            trackingKey: TrackingKey.ExperimentStarted,
          }
          if (!currentState) {
            currentState = store.getState()
          }
          logExperimentActivated(action.payload, experiment.variantName || Original, {
            company_uuid: configCompanyUuidSelector(currentState),
            product_type: configProductSelector(currentState),
            results_uuid: configResultsUuidSelector(currentState),
          })
        }
        shouldUpdateContext = true
        break
      case experimentActions.upsertExperiments.type:
        shouldUpdateContext = true
        break
      case experimentActions.reportError.type:
        shouldUpdateContext = true
        break
      default:
    }
    currentState = undefined
    const nextResult = next(action)

    if (shouldUpdateContext) {
      if (!currentState) {
        currentState = store.getState()
      }
      datadogRum.addRumGlobalContext(RUMContextKeys.DD_CTX_EXPERIMENTS, experimentStateSelector(currentState))
    }

    return nextResult
  }
